import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Home, Ticket, Settings, Bug, LogOut, Shield, Users, ChevronUp, ClipboardList } from '../../theme/icons.js';
import { ThemeToggle } from '../ui/ThemeToggle.js';
import { FeedbackSheet } from '../../features/feedback/FeedbackSheet.js';
import { useEvent } from '../../contexts/EventContext.js';
import { Select } from '../ui/Select.js';
import { DvLogo } from '../ui/DvLogo.js';
import { API } from '../../App.js';
import { useFeedback } from '../../hooks/useFeedback.js';
import { neuesteZuerst } from '../../features/events/datum.js';
import './SideNav.css';

function NavItem({ icon, label, active, onClick }) {
  return (
    <button
      className={`sidenav-item ${active ? 'active' : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      <span className="sidenav-item-icon">{icon}</span>
      <span className="sidenav-item-label">{label}</span>
    </button>
  );
}

export function SideNav({ page, onNavigate, onLogout, role, isOpen, onClose }) {
  const feedbackMail = useFeedback();
  /* Bietet der Server Feedback → GitHub-Issue an, öffnet der Knopf das
     Formular, sonst wie bisher die Mail. Einmal beim Laden gefragt; schlägt
     die Frage fehl, bleibt es bei der Mail. */
  const [feedbackPerIssue, setFeedbackPerIssue] = useState(false);
  const [feedbackOffen, setFeedbackOffen] = useState(false);
  useEffect(() => {
    let lebt = true;
    API.getFeedbackEnabled?.()
      .then((an) => lebt && setFeedbackPerIssue(!!an))
      .catch(() => {});
    return () => {
      lebt = false;
    };
  }, []);
  const feedbackMelden = () => (feedbackPerIssue ? setFeedbackOffen(true) : feedbackMail());
  const { events, selectedEvent, setSelectedEvent } = useEvent();
  // #check-in-store — Check-in nur bei whole-event-Zugang zeigen:
  // Superadmin (global) oder mainGroup-delegierter Admin der gewählten
  // Veranstaltung. Sub-Gruppen-Grants sehen die Oberfläche nicht — "nichts zu
  // sehen = nicht anzeigen". Status ist pro Veranstaltung, daher nach dem
  // Event-Wechsel async neu geprüft; Superadmin ohne Wartezeit.
  const [canCheckIn, setCanCheckIn] = useState(role === 'superadmin');
  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (role === 'superadmin') {
        setCanCheckIn(true);
        return;
       }
      if (!selectedEvent?.slug) {
        setCanCheckIn(false);
        return;
       }
      const ok = await API.isAdmin(selectedEvent.organizer, selectedEvent.slug);
      if (!cancelled) setCanCheckIn(!!ok);
     }
    check();
    return () => {
      cancelled = true;
     };
   }, [selectedEvent?.slug, selectedEvent?.organizer, role]);
  const [profile, setProfile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const profileRef = useRef(null);
  const menuRef = useRef(null);
  const profileBtnRef = useRef(null);

  // Fetch the logged-in user's NaMi name for the profile area.
  useEffect(() => {
    let alive = true;
    API.getCallerProfile?.()
      .then((p) => {
        if (alive) setProfile(p);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Close the profile menu on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDocPointer(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [menuOpen]);

  // Move focus into the menu when it opens (keyboard accessibility).
  useEffect(() => {
    if (menuOpen) menuRef.current?.querySelector('.sidenav-profile-menu-item')?.focus();
  }, [menuOpen]);

  // Keyboard navigation within the profile menu (Esc closes, arrows move, Tab closes).
  function onMenuKeyDown(e) {
    const items = Array.from(menuRef.current?.querySelectorAll('.sidenav-profile-menu-item') || []);
    if (!items.length) return;
    const idx = items.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      setMenuOpen(false);
      profileBtnRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length].focus();
    } else if (e.key === 'Tab') {
      setMenuOpen(false);
    }
  }

  const roleLabel =
    {
      superadmin: 'Superadmin',
      groupAdmin: 'Gruppen-Admin',
      /* Event-Zugriff ohne Admin-Rechte — hatte als einzige Rolle keine
         Zeile unter dem Namen (Audit B12). */
      granted: 'Zugriff',
      self: 'Mitglied',
      limited: 'Eingeschränkt',
    }[role] || '';

  const fullName = profile?.name ? `${profile.name} ${profile.surname || ''}`.trim() : '';
  const displayName = fullName || roleLabel || 'Angemeldet';
  const initials = fullName
    ? fullName
        .split(/\s+/)
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'DV';

  return (
    <nav className={`sidenav ${isOpen ? 'open' : ''}`} aria-label="Hauptnavigation">
      {/* Brand: logo + tool name */}
      <div className="sidenav-brand">
        <div className="sidenav-logo">
          <DvLogo groesse="klein" hellKlasse="sidenav-logo--light" dunkelKlasse="sidenav-logo--dark" />
        </div>
        {/* Wortmarke wie im Kopf der Website: .logo-text, Arvo in
            Großbuchstaben, der zweite Teil leichter und in DPSG-Blau. */}
        <div className="logo-text">
          DV Limburg <span>Dashboard</span>
        </div>
      </div>

      {/* Event selector */}
      {events.length > 0 && role !== 'self' && (
        <div className="sidenav-event-wrap">
          <div className="sidenav-event-label">Veranstaltung</div>
          <Select
            ariaLabel="Veranstaltung wählen"
            value={selectedEvent?.slug || ''}
            onChange={(slug) => setSelectedEvent(events.find((ev) => ev.slug === slug))}
            /* Chronologisch wie auf der Startseite: am weitesten in der
               Zukunft oben, am längsten vorbei unten. Kopie, weil sort()
               sonst die Liste im EventContext umstellt. */
            options={[...events]
              .sort(neuesteZuerst)
              .map((ev) => ({ value: ev.slug, label: ev.name.de }))}
            searchable
          />
        </div>
      )}

      {/* Main nav */}
      <div className="sidenav-section">
        {/* Start steht allen offen: die Übersicht zeigt, wofür man angemeldet
            ist, und das gilt auch für reine Teilnehmende. */}
        <NavItem
          icon={<Home size={20} />}
          label="Start"
          active={page === 'start'}
          onClick={() => {
            onNavigate('start');
            onClose?.();
          }}
        />
        {role !== 'self' && (
          <NavItem
            icon={<Ticket size={20} />}
            label="Anmeldungen"
            active={page === 'dashboard'}
            onClick={() => {
              onNavigate('dashboard');
              onClose?.();
            }}
          />
        )}
        {(role === 'groupAdmin' || role === 'superadmin') && (
          <NavItem
            icon={<Users size={20} />}
            label="Personen"
            active={page === 'persons'}
            onClick={() => {
              onNavigate('persons');
              onClose?.();
            }}
          />
        )}
         {/* "Meine Anmeldung" gehört auch granted User (Issue #63): sie
             behalten den Admin-"Anmeldungen"-Zugang, brauchen aber zusätzlich
             die eigene Registrierung. */}
        {(role === 'self' || role === 'granted') && (
          <NavItem
            icon={<Users size={20} />}
            label="Meine Anmeldung"
            active={page === 'my'}
            onClick={() => {
              onNavigate('my');
              onClose?.();
            }}
          />
        )}
        {/* #check-in-store U6 — per-Teilnehmer-Anwesenheit: nur bei
           whole-event-Zugang (Superadmin oder mainGroup-delegierter Admin der
           gewählten Veranstaltung). Sub-Gruppen-Grants sehen den Tab nicht —
           "nichts zu sehen = nicht anzeigen". */}
        {canCheckIn && (
          <NavItem
           icon={<ClipboardList size={20} />}
           label="Check-in"
           active={page === 'checkin'}
           onClick={() => {
            onNavigate('checkin');
            onClose?.();
            }}
          />
         )}
        {/* Einstellungen sind ein Hauptbereich und stehen in der Navigation,
            nicht hinter dem eigenen Namen — vorher zwei Klicks, versteckt im
            Profil-Menü (Audit B8, #96). Das Profil-Menü behält „Abmelden". */}
        {role === 'superadmin' && (
          <NavItem
            icon={<Settings size={20} />}
            label="Einstellungen"
            active={page === 'settings'}
            onClick={() => {
              onNavigate('settings');
              onClose?.();
            }}
          />
        )}
      </div>

      {/* Quick actions: Design (theme) + Feedback as round icon buttons */}
      <div className="sidenav-quick">
        <ThemeToggle className="sidenav-icon-btn" />
        <button
          className="sidenav-icon-btn"
          onClick={feedbackMelden}
          aria-label="Feedback / Problem"
          title="Feedback / Problem"
        >
          <Bug size={20} />
        </button>
      </div>
      {/* Ins <body> statt in die Leiste: Auf dem Telefon ist die Leiste eine
          verschobene Schublade (transform), und darin wäre position: fixed
          des Sheets an die Leiste gebunden statt ans Fenster. */}
      {createPortal(
        <FeedbackSheet
          open={feedbackOffen}
          onClose={() => setFeedbackOffen(false)}
          onSenden={(feedback) => API.sendFeedback(feedback)}
          kontext={{
            page,
            event: selectedEvent ? `${selectedEvent.organizer}/${selectedEvent.slug}` : '',
            role,
          }}
        />,
        document.body
      )}

      {/* Profile (bottom) with dropdown menu */}
      <div className="sidenav-profile-wrap" ref={profileRef}>
        {menuOpen && (
          <div className="sidenav-profile-menu" role="menu" ref={menuRef} onKeyDown={onMenuKeyDown}>
            <button className="sidenav-profile-menu-item" role="menuitem" onClick={onLogout}>
              <LogOut size={18} /> Abmelden
            </button>
          </div>
        )}
        <button
          ref={profileBtnRef}
          className="sidenav-profile"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="sidenav-avatar">{initials}</span>
          <span className="sidenav-profile-text">
            <span className="sidenav-profile-name">{displayName}</span>
            {roleLabel && (
              <span className="sidenav-profile-role">
                <Shield size={10} /> {roleLabel}
              </span>
            )}
          </span>
          <ChevronUp
            size={16}
            className={`sidenav-profile-chevron ${menuOpen ? 'open' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>
    </nav>
  );
}
